import React from 'react';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';

import LoadingSpinner from 'components/ui/loading_spinner';
import { searchUsers } from 'components/users/api';
import ProjectShowcaseContacts from 'bundles/showcase_contacts/project';
import { filter, find, without } from 'lodash';

export default class ProjectCollaboratorModal extends React.Component {
  static propTypes = {
    modalTitle: PropTypes.string,
    projectUsers: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      imageUrl: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      notificationsBlocked: PropTypes.bool,
      removeable: PropTypes.bool,
      role: PropTypes.string.isRequired
    })),
    show: PropTypes.bool,
    onDone: PropTypes.func.isRequired,
    onUpdateCollaborators: PropTypes.func.isRequired,
    onUpdateNotificationsBlockedUserIds: PropTypes.func.isRequired
  };

  state = {
    addingCollaborators: false,
    removingCollaborator: false,
    savingCollaborator: false,
    savingNotificationPreference: false
  }

  isBroker = user => user.role.match(/broker/i)
  brokers = () => filter(this.props.projectUsers, this.isBroker)
  nonBrokers = () => filter(this.props.projectUsers, user => !this.isBroker(user))
  userDefinedCollaborators = () => this.nonBrokers().filter(user => user.removeable)

  toggleNotificationPreference = (user) => {
    let blockedUserIds = this.props.projectUsers.filter(u => u.notificationsBlocked).map(u => u.id);

    if (blockedUserIds.includes(user.id)) {
      blockedUserIds = without(blockedUserIds, user.id);
    } else {
      blockedUserIds.push(user.id);
    }

    this.setState({ savingNotificationPreference: true });

    this.props.onUpdateNotificationsBlockedUserIds(blockedUserIds)
      .then(() => this.setState({ savingNotificationPreference: false }));
  }

  addCollaborator = (user) => {
    this.setState({ savingCollaborator: user.name, addingCollaborators: false });

    this.props.onUpdateCollaborators([
      ...this.userDefinedCollaborators(),
      user
    ]).then(() => this.setState({ savingCollaborator: false }));
  }

  removeCollaborator = ({ id }) => {
    this.setState({ removingCollaborator: id });

    this.props.onUpdateCollaborators(
      filter(this.userDefinedCollaborators(), user => user.id !== id)
    ).then(() => this.setState({ removingCollaborator: false }));
  }

  canRemoveUser = (user) => {
    return user.removeable && !this.isBroker(user);
  }

  searchUsersForAsync = (term) => {
    return searchUsers(term).then(({ users }) => {
      const foundUsers = users.map(user => ({ label: user.name, value: user.id, ...user }));
      return foundUsers.filter(user => !find(this.props.projectUsers, { id: user.id }));
    });
  }

  renderAddCollaborator() {
    if (!this.state.addingCollaborators) {
      return (
        <tr>
          <td className="border-0" colSpan="5">
            <Button
              disabled={this.state.savingCollaborator}
              name="add-collaborator"
              variant="secondary"
              onClick={() => this.setState({ addingCollaborators: true })}
            >
              {this.state.savingCollaborator ? 'Saving..' : '+ Add a Collaborator'}
            </Button>
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td className="border-0 pl-0 align-middle" colSpan="4">
          <AsyncSelect
            autoFocus={true}
            className="grow"
            loadOptions={this.searchUsersForAsync}
            name="user-select"
            onChange={this.addCollaborator}
          />
        </td>
        <td className="text-right border-0 align-middle">
          <FontAwesomeIcon
            className="text-danger clickable"
            icon={faTimes}
            onClick={() => this.setState({ addingCollaborators: false })}
          />
        </td>
      </tr>
    );
  }

  renderUserActions(user) {
    if (this.state.removingCollaborator === user.id) {
      return <LoadingSpinner inline size="md" />;
    }

    if (this.canRemoveUser(user)) {
      return (
        <span
          className="text-danger clickable"
          name="remove-user"
          onClick={() => this.removeCollaborator(user)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </span>
      );
    }
  }

  renderUserRow(user) {
    return (
      <tr key={user.id} name="user-row">
        <td className="px-0 border-0 align-middle">
          <span className="avatar avatar-lg">
            <img className="avatar-image" src={user.imageUrl} />
          </span>
        </td>
        <td className="w-100 border-0 align-middle">
          <span>
            <div className="fw-bold">{user.name}</div>
            <div className="text-muted small">{user.email}</div>
          </span>
        </td>
        <td className="border-0 px-5 align-middle">
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              type="checkbox"
              checked={!user.notificationsBlocked}
              disabled={this.state.savingNotificationPreference}
              id={`user-notifications-${user.id}`}
              label="Notifications"
              name={`toggle-notifications-${user.id}`}
              onChange={() => this.toggleNotificationPreference(user)}
            />
            <div className="pl-2">
              <OverlayTrigger
                overlay={
                  <Tooltip id={`toggle-notifications-${user.id}`}>
                    <span>When this box is checked, this user will receive notifications for this listing. </span>
                    <span>They will only receive the type of notifications that are selected in their profile.</span>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </OverlayTrigger>
            </div>
          </div>
        </td>
        <td className="text-right border-0 align-middle">
          <i>{user.role}</i>
        </td>
        <td className="text-right border-0 align-middle" >
          {this.renderUserActions(user)}
        </td>
      </tr>
    );
  }

  renderBody() {
    return (
      <React.Fragment>
        <table className="table my-2">
          <tbody>
            {this.brokers().map(b => this.renderUserRow(b))}
            {this.nonBrokers().map(b => this.renderUserRow(b))}
            {this.renderAddCollaborator()}
          </tbody>
        </table>
        <ProjectShowcaseContacts/>
      </React.Fragment>
    );
  }

  render() {
    const { modalTitle, show, onDone } = this.props;
    return (
      <Modal
        className="overflow-visible"
        keyboard={true}
        show={show}
        size="lg"
        onHide={onDone}
      >
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderBody()}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button name="ok" variant="primary" onClick={onDone}>Done</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
